import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import useSubscription from '../../hooks/useSubscription';
import './CRWS.css';

import noThumbnail from './icons/default.jpg';
import useCardColor from '../../hooks/useCardColor';
import CrwsDetailDialog from './components/CrwsDetailDialog';
import Subtitle from './components/Subtitle';
import GetDomainTheme from './functions/GetDomainTheme';
import AssetStatus from '../assetStatus/AssetStatus';
import VerifiedIcon from '../VerifiedIcon/VerifiedIcon';
import AssetRating from '../AssetRating/AssetRating';
import QuickSelectIcons from '../QuickSelectIcons/QuickSelectIcons';
import CrwsTitle from '../CrwsTitle/CrwsTitle';
import getString from '../../hooks/getString';
import DialogButton from '../DialogButton/DialogButton';

const CRWS = ({ crws: asset, viewingSubscribed, searchTerm }) => {
  const {
    abstract,
    title,
    subscribed,
    assetId,
    domains,
    contentMaturityReason,
    copyright,
    assetMasterContent,
    contentMaturityColor,
    versions,
    sources,
    dateImported,
    dateLastUpdated,
    areas,
  } = asset;
  const { OPRShort: oprShort } = sources.values;

  const { DateEffective: dateEffective, DescriptionofVersion: version } =
    versions.values;

  const { thumbnail } = assetMasterContent || {};
  // const [expanded, setExpanded] = React.useState(false);

  const cardDetails =
    domains && domains.values ? GetDomainTheme(domains.values) : {};
  const tabIndex = 0;
  const classes = useCardColor(cardDetails.color);
  const { toggleSubscription, isSubscribed } = useSubscription(subscribed);
  const showCard = (viewingSubscribed && isSubscribed) || !viewingSubscribed;
  const [openDetails, setOpenDetails] = useState(false);

  const handleOpenDialog = () => {
    setOpenDetails(true);
  };

  const headers = {
    delimiter: <br />,
    fields: [{ name: title.name, value: title.value }],
  };

  return (
    <>
      {showCard && (
        <>
          <div
            role="button"
            tabIndex={tabIndex}
            className={classes.card}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                handleOpenDialog();
              }
            }}
          >
            <Card raised className={classes.card}>
              <CardActionArea
                onClick={handleOpenDialog}
                className={classes.content}
              >
                <CardHeader
                  className={classes.cardHeader}
                  // avatar={
                  //   <AssetAvatar
                  //     domain={domains}
                  //     classes={classes}
                  //     cardDetails={cardDetails}
                  //     elevation={3}
                  //   />
                  // }
                  title={
                    <div style={{ width: '200px' }}>
                      <CrwsTitle
                        asset={asset}
                        headers={headers}
                        oprShort={oprShort}
                      />
                    </div>
                  }
                  action={
                    <VerifiedIcon
                      size={50}
                      reason={contentMaturityReason.value}
                      color={contentMaturityColor.value}
                    />
                  }
                />
                <div style={{ position: 'relative' }}>
                  <CardMedia
                    variant="outlined"
                    title={title.value}
                    className={classes.cardMedia}
                    onClick={handleOpenDialog}
                    tabIndex={0}
                    role="button"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={
                        thumbnail || (asset.rendition_content.preview_content ? asset.rendition_content.preview_content.url : noThumbnail)
                        // (controlledAccess.value === 'Yes'
                        //   ? controlledAccessThumbnail
                        //   : noThumbnail)
                      }
                      alt="Thumbnail"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        flexShrink: '0',
                      }}
                    />
                  </CardMedia>{' '}
                  <div
                    style={{
                      position: 'absolute',
                      left: 10,
                      top: 0,
                    }}
                  >
                    <AssetStatus
                      dateImported={dateImported}
                      dateLastUpdated={dateLastUpdated}
                      cardDetails={cardDetails}
                    />
                  </div>
                </div>
                <CardContent className={classes.content}>
                  <Subtitle dateEffective={dateEffective} version={version} />
                  <Typography variant="caption">
                    <b>Copyright:</b> {copyright.value}
                  </Typography>

                  <Typography variant="caption" display="block" noWrap>
                    <b>Areas:</b> {getString(areas.values.Area.values, ',')}
                  </Typography>

                  <Typography variant="caption" display="block" noWrap>
                    <b>Cross-Cutting Enablers:</b>{' '}
                    {getString(
                      domains.values['Cross-CuttingEnabler'].values,
                      ','
                    )}
                  </Typography>
                </CardContent>
              </CardActionArea>

              <CardActions className={classes.content}>
                <AssetRating asset={asset} classes={classes} />
              </CardActions>
              <CardActions disableSpacing className={classes.content}>
                <QuickSelectIcons
                  asset={asset}
                  classes={classes}
                  cardDetails={cardDetails}
                  isSubscribed={isSubscribed}
                  toggleSubscription={toggleSubscription}
                />

                {abstract.value && (
                  <DialogButton
                    title="Abtract"
                    content={abstract.value}
                    component={IconButton}
                  >
                    <ExpandMoreIcon />
                  </DialogButton>
                )}
              </CardActions>
              {/* <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent className={classes.content}>
                  <RenderAbstract abstract={abstract} classes={classes} />
                </CardContent>
              </Collapse> */}
            </Card>
          </div>
          <CrwsDetailDialog
            assetId={assetId}
            openDetails={openDetails}
            setOpenDetails={setOpenDetails}
            crws={asset}
            isSubscribed={isSubscribed}
            toggleSubscription={toggleSubscription}
            searchTerm={searchTerm}
            headers={headers}
            classes={classes}
          />
        </>
      )}
    </>
  );
};

CRWS.propTypes = {
  crws: PropTypes.object.isRequired,
  viewingSubscribed: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string,
};

CRWS.defaultProps = {
  searchTerm: undefined,
};

export default CRWS;
